import React from 'react'
import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'

import FileUpload from '../../components/form/FileUpload'
import Button from '../../components/form/Button'
import Loading from '../../components/layout/Loading'
import ErrorMessage from '../../components/errors/ErrorMessage'
import Collapsable from '../../components/layout/Collapsable'

import { useI18N } from '../../../libraries/i18n/hook'
import { useToaster } from '../../../libraries/toaster'
import { uploadUserAcademicTranscript, getUserAcademicTranscript } from '../../../api/users'

import type { UserAcademicTranscript } from '../../../api/users/types'

const UserAcademicTranscriptSection: React.FC = () => {
  const { t } = useI18N()
  const { showSuccess, showError } = useToaster()

  const {
    data: academicTranscript,
    isLoading: isTranscriptLoading,
    error: transcriptError,
    mutate: refreshTranscript,
  } = useSWR<UserAcademicTranscript | null>('/users/academic-transcript', getUserAcademicTranscript)

  const [selectedTranscriptFile, setSelectedTranscriptFile] = React.useState<File | null>(null)
  const [fileUploadKey, setFileUploadKey] = React.useState(0)

  const {
    trigger: triggerUploadTranscript,
    isMutating: isUploading,
    error: uploadTranscriptError,
  } = useSWRMutation('/users/academic-transcript', uploadUserAcademicTranscript, {
    onSuccess: async (data) => {
      await refreshTranscript(data, { revalidate: false })
      setSelectedTranscriptFile(null)
      setFileUploadKey((k) => k + 1)
      showSuccess(t('academic.transcript.uploaded'))
    },
    onError: (err) => {
      showError(err instanceof Error ? err.message : t('error.unexpected'))
    },
  })

  const handleUpdateTranscript = async () => {
    if (!selectedTranscriptFile) return
    await triggerUploadTranscript({ file: selectedTranscriptFile })
  }

  if (transcriptError) return <ErrorMessage message={transcriptError.toString()} />

  return (
    <section className='w-full'>
      <Collapsable
        size='lg'
        label={(
          <div className='flex-1 flex justify-between items-center pr-2'>
            <span>{t('profile.academicTranscript.title')}</span>
          </div>
        )}
      >
        {isTranscriptLoading && <Loading />}
        {!isTranscriptLoading && (
          <div>
            <FileUpload
              key={fileUploadKey}
              onFileSelected={setSelectedTranscriptFile}
              label=''
              accept='.pdf'
              defaultButtonLabel={{
                select: t('common.file.select'),
                change: t('common.file.change'),
              }}
              disabled={isUploading}
              defaultFile={academicTranscript?.srcUrl ? { url: academicTranscript.srcUrl, label: t('profile.academicTranscript.latest') } : undefined}
              maxMbSize={1}
            />

            <div className='text-center mt-4'>
              {isUploading ? (
                <div className='text-center flex justify-center items-center'>
                  <Loading /><small>{t('academic.transcript.upload.processing')}</small>
                </div>
              ) : selectedTranscriptFile ? (
                <Button
                  type='button'
                  onClick={handleUpdateTranscript}
                  disabled={!selectedTranscriptFile || isUploading}
                  size='sm'
                >
                  {t('common.save')}
                </Button>
              ): null}
            </div>

            {uploadTranscriptError ? (
              <ErrorMessage
                message={uploadTranscriptError instanceof Error ? uploadTranscriptError.message : uploadTranscriptError.toString()}
              />
            ) : null}
          </div>
        )}
      </Collapsable>
    </section>
  )
}

export default UserAcademicTranscriptSection

